import React, { useState, useEffect } from "react";
import { getDatabase, ref, onValue, query, orderByChild, get } from "../../Config/matuRtdb";
import {
  MessageCircle,
  Clock,
  Users,
  TrendingUp,
  TrendingDown,
  Star,
  AlertCircle,
  CheckCircle,
  XCircle,
  Calendar,
  BarChart3,
  Activity,
  Zap,
  Target,
  Award,
  Sparkles,
  Heart,
  ThumbsUp,
  ThumbsDown
} from "lucide-react";

export default function SupportMetrics() {
  const [metrics, setMetrics] = useState({
    totalChats: 0,
    activeChats: 0,
    resolvedChats: 0,
    averageResponseTime: 0,
    customerSatisfaction: 0,
    totalMessages: 0,
    todayChats: 0,
    weeklyChats: 0,
    monthlyChats: 0
  });
  const [responseTimes, setResponseTimes] = useState([]);
  const [chatTrends, setChatTrends] = useState([]);
  const [topIssues, setTopIssues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const db = getDatabase();

  useEffect(() => {
    loadSupportMetrics();
  }, []);

  const loadSupportMetrics = () => {
    const chatsRef = ref(db, "chats");
    onValue(chatsRef, (snapshot) => {
      if (snapshot.exists()) {
        const chatsData = [];
        const responseTimesData = [];
        const now = Date.now();
        const today = new Date().setHours(0, 0, 0, 0);
        const weekAgo = today - 7 * 24 * 60 * 60 * 1000;
        const monthAgo = today - 30 * 24 * 60 * 60 * 1000;

        let totalChats = 0;
        let activeChats = 0;
        let resolvedChats = 0;
        let todayChats = 0;
        let weeklyChats = 0;
        let monthlyChats = 0;
        let totalMessages = 0;
        let totalResponseTime = 0;
        let responseCount = 0;

        snapshot.forEach((childSnapshot) => {
          const userId = childSnapshot.key;
          const chatData = childSnapshot.val();
          const messages = Object.values(chatData || {});
          
          totalChats++;
          totalMessages += messages.length;

          // Calcular tiempos de respuesta
          let firstUserMessage = null;
          let firstAdminResponse = null;
          
          messages.forEach((message) => {
            if (!firstUserMessage && message.sender !== "admin") {
              firstUserMessage = message.timestamp;
            }
            if (firstUserMessage && !firstAdminResponse && message.sender === "admin") {
              firstAdminResponse = message.timestamp;
              const responseTime = firstAdminResponse - firstUserMessage;
              responseTimesData.push(responseTime);
              totalResponseTime += responseTime;
              responseCount++;
            }
          });

          // Contar chats por período
          const lastMessage = messages[messages.length - 1];
          if (lastMessage?.timestamp >= today) {
            todayChats++;
          }
          if (lastMessage?.timestamp >= weekAgo) {
            weeklyChats++;
          }
          if (lastMessage?.timestamp >= monthAgo) {
            monthlyChats++;
          }

          // Determinar estado del chat
          if (lastMessage?.sender === "admin") {
            resolvedChats++;
          } else {
            activeChats++;
          }

          chatsData.push({
            userId,
            messages,
            lastMessage: lastMessage?.timestamp || 0
          });
        });

        // Calcular métricas
        const averageResponseTime = responseCount > 0 ? totalResponseTime / responseCount : 0;
        const customerSatisfaction = calculateSatisfaction(chatsData);

        setMetrics({
          totalChats,
          activeChats,
          resolvedChats,
          averageResponseTime,
          customerSatisfaction,
          totalMessages,
          todayChats,
          weeklyChats,
          monthlyChats
        });

        setResponseTimes(responseTimesData);
        setChatTrends(generateChatTrends(chatsData));
        setTopIssues(analyzeTopIssues(chatsData));
        setIsLoading(false);
      }
    });
  };

  const calculateSatisfaction = (chatsData) => {
    // Simulación de satisfacción basada en tiempo de respuesta y resolución
    let satisfaction = 0;
    let totalChats = chatsData.length;

    chatsData.forEach((chat) => {
      const messages = chat.messages;
      const hasAdminResponse = messages.some(m => m.sender === "admin");
      const responseTime = getResponseTime(messages);
      
      if (hasAdminResponse && responseTime < 300000) { // 5 minutos
        satisfaction += 1;
      } else if (hasAdminResponse && responseTime < 900000) { // 15 minutos
        satisfaction += 0.8;
      } else if (hasAdminResponse) {
        satisfaction += 0.6;
      } else {
        satisfaction += 0.3;
      }
    });

    return totalChats > 0 ? (satisfaction / totalChats) * 100 : 0;
  };

  const getResponseTime = (messages) => {
    let firstUserMessage = null;
    let firstAdminResponse = null;
    
    messages.forEach((message) => {
      if (!firstUserMessage && message.sender !== "admin") {
        firstUserMessage = message.timestamp;
      }
      if (firstUserMessage && !firstAdminResponse && message.sender === "admin") {
        firstAdminResponse = message.timestamp;
      }
    });

    return firstAdminResponse && firstUserMessage ? firstAdminResponse - firstUserMessage : 0;
  };

  const generateChatTrends = (chatsData) => {
    const trends = [];
    const now = Date.now();
    const dayInMs = 24 * 60 * 60 * 1000;

    for (let i = 6; i >= 0; i--) {
      const date = new Date(now - i * dayInMs);
      const dayStart = date.setHours(0, 0, 0, 0);
      const dayEnd = dayStart + dayInMs;
      
      const dayChats = chatsData.filter(chat => {
        const lastMessage = chat.messages[chat.messages.length - 1];
        return lastMessage?.timestamp >= dayStart && lastMessage?.timestamp < dayEnd;
      }).length;

      trends.push({
        date: date.toLocaleDateString("es-ES", { weekday: "short" }),
        chats: dayChats
      });
    }

    return trends;
  };

  const analyzeTopIssues = (chatsData) => {
    const issues = {};
    
    chatsData.forEach((chat) => {
      chat.messages.forEach((message) => {
        const text = message.text.toLowerCase();
        
        if (text.includes("problema") || text.includes("error")) {
          issues["Problemas técnicos"] = (issues["Problemas técnicos"] || 0) + 1;
        }
        if (text.includes("pago") || text.includes("dinero") || text.includes("vip")) {
          issues["Problemas de pago"] = (issues["Problemas de pago"] || 0) + 1;
        }
        if (text.includes("cuenta") || text.includes("registro")) {
          issues["Problemas de cuenta"] = (issues["Problemas de cuenta"] || 0) + 1;
        }
        if (text.includes("pronóstico") || text.includes("predicción")) {
          issues["Consultas de pronósticos"] = (issues["Consultas de pronósticos"] || 0) + 1;
        }
        if (text.includes("app") || text.includes("aplicación")) {
          issues["Problemas de la app"] = (issues["Problemas de la app"] || 0) + 1;
        }
      });
    });

    return Object.entries(issues)
      .map(([issue, count]) => ({ issue, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  };

  const formatTime = (ms) => {
    const minutes = Math.floor(ms / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m`;
    return "Menos de 1m";
  };

  const getSatisfactionColor = (satisfaction) => {
    if (satisfaction >= 80) return "text-green-400";
    if (satisfaction >= 60) return "text-yellow-400";
    return "text-red-400";
  };

  const getSatisfactionIcon = (satisfaction) => {
    if (satisfaction >= 80) return <Star className="text-green-400" />;
    if (satisfaction >= 60) return <AlertCircle className="text-yellow-400" />;
    return <XCircle className="text-red-400" />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Métricas principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-400">Total Chats</p>
              <p className="text-3xl font-bold text-white">{metrics.totalChats}</p>
            </div>
            <div className="p-3 bg-blue-900/50 rounded-lg border border-blue-700">
              <MessageCircle className="h-6 w-6 text-blue-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="h-4 w-4 text-green-400 mr-1" />
            <span className="text-green-400">+{metrics.todayChats} hoy</span>
          </div>
        </div>

        <div className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-400">Chats Activos</p>
              <p className="text-3xl font-bold text-orange-400">{metrics.activeChats}</p>
            </div>
            <div className="p-3 bg-orange-900/50 rounded-lg border border-orange-700">
              <Activity className="h-6 w-6 text-orange-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <Clock className="h-4 w-4 text-orange-400 mr-1" />
            <span className="text-orange-400">Pendientes de respuesta</span>
          </div>
        </div>

        <div className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-400">Tiempo Respuesta</p>
              <p className="text-3xl font-bold text-purple-400">
                {formatTime(metrics.averageResponseTime)}
              </p>
            </div>
            <div className="p-3 bg-purple-900/50 rounded-lg border border-purple-700">
              <Zap className="h-6 w-6 text-purple-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <Target className="h-4 w-4 text-purple-400 mr-1" />
            <span className="text-purple-400">Promedio</span>
          </div>
        </div>

        <div className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-400">Satisfacción</p>
              <p className={`text-3xl font-bold ${getSatisfactionColor(metrics.customerSatisfaction)}`}>
                {Math.round(metrics.customerSatisfaction)}%
              </p>
            </div>
            <div className="p-3 bg-green-900/50 rounded-lg border border-green-700">
              {getSatisfactionIcon(metrics.customerSatisfaction)}
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <Award className="h-4 w-4 text-green-400 mr-1" />
            <span className="text-green-400">Calificación</span>
          </div>
        </div>
      </div>

      {/* Gráficos y análisis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tendencia de chats */}
        <div className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4">Tendencia de Chats (Últimos 7 días)</h3>
          <div className="space-y-3">
            {chatTrends.map((trend, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-300">{trend.date}</span>
                <div className="flex items-center gap-2">
                  <div className="w-32 bg-slate-700 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${Math.min((trend.chats / Math.max(...chatTrends.map(t => t.chats))) * 100, 100)}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-white w-8 text-right">
                    {trend.chats}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top issues */}
        <div className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4">Principales Temas de Consulta</h3>
          <div className="space-y-4">
            {topIssues.map((issue, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${
                    index === 0 ? "bg-red-500" :
                    index === 1 ? "bg-orange-500" :
                    index === 2 ? "bg-yellow-500" :
                    index === 3 ? "bg-blue-500" : "bg-slate-500"
                  }`}></div>
                  <span className="text-sm font-medium text-slate-300">{issue.issue}</span>
                </div>
                <span className="text-sm font-bold text-white">{issue.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Estadísticas detalladas */}
      <div className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
        <h3 className="text-lg font-semibold text-white mb-4">Estadísticas Detalladas</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-400">{metrics.todayChats}</div>
            <div className="text-sm text-slate-400">Chats Hoy</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-400">{metrics.weeklyChats}</div>
            <div className="text-sm text-slate-400">Esta Semana</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-400">{metrics.monthlyChats}</div>
            <div className="text-sm text-slate-400">Este Mes</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-400">{metrics.totalMessages}</div>
            <div className="text-sm text-slate-400">Total Mensajes</div>
          </div>
        </div>
      </div>

      {/* KPIs de rendimiento */}
      <div className="bg-slate-800 p-6 rounded-xl shadow-lg border border-slate-700">
        <h3 className="text-lg font-semibold text-white mb-4">KPIs de Rendimiento</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-blue-900/20 rounded-lg border border-blue-700">
            <div className="text-3xl font-bold text-blue-400">
              {metrics.totalChats > 0 ? Math.round((metrics.resolvedChats / metrics.totalChats) * 100) : 0}%
            </div>
            <div className="text-sm text-blue-400 font-medium">Tasa de Resolución</div>
            <div className="text-xs text-slate-400 mt-1">
              {metrics.resolvedChats} de {metrics.totalChats} chats resueltos
            </div>
          </div>
          
          <div className="text-center p-4 bg-green-900/20 rounded-lg border border-green-700">
            <div className="text-3xl font-bold text-green-400">
              {metrics.totalChats > 0 ? Math.round((metrics.totalMessages / metrics.totalChats)) : 0}
            </div>
            <div className="text-sm text-green-400 font-medium">Mensajes por Chat</div>
            <div className="text-xs text-slate-400 mt-1">
              Promedio de interacción
            </div>
          </div>
          
          <div className="text-center p-4 bg-purple-900/20 rounded-lg border border-purple-700">
            <div className="text-3xl font-bold text-purple-400">
              {metrics.averageResponseTime < 300000 ? "Excelente" :
               metrics.averageResponseTime < 900000 ? "Bueno" :
               metrics.averageResponseTime < 1800000 ? "Regular" : "Necesita Mejora"}
            </div>
            <div className="text-sm text-purple-400 font-medium">Calidad de Respuesta</div>
            <div className="text-xs text-slate-400 mt-1">
              Basado en tiempo promedio
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 